// A self-contained QR encoder (byte mode), because these codes get PRINTED.
//
// The alternative was api.qrserver.com. That was fine for one code on screen; it
// is the wrong dependency for a sheet of 200 signs that someone is about to
// laminate and screw to a wall. Bulk printing has to work offline, has to be
// vector (a scaled-up PNG prints fuzzy, and fuzzy is unscannable), and must not
// silently produce 200 broken codes because a request was throttled. It is also
// fixed at error-correction level L, which leaves no budget for the UMaps mark
// that belongs in the middle of every code this product makes — so nothing
// reaches for it any more except QrPopup's copy-out fallback.
//
// ⚠️ NOTHING HERE DRAWS THE MARK. `qrSvg` and `qrDataUrl` return a bare code.
// Callers want `utils/qrBadge.js`, which wraps this and is the one place the
// badge geometry lives; a screen showing a bare code beside a badged one reads
// as a bug in the badged one.
//
// Byte mode + error-correction level M (recovers ~15%) is the right trade for
// signage: enough redundancy to survive a scuffed or partly-covered sign without
// inflating the module count so far that the code needs a bigger sticker.
//
// Implements ISO/IEC 18004 for versions 1–15. At level M that tops out around
// 412 bytes — far more than the ~40-character URLs here, with room to spare for
// a long custom base URL.

/** Data codewords per block, per version, at EC level M: [ecPerBlock, groups]. */
const EC_M = {
  1:  [10, [[1, 16]]],
  2:  [16, [[1, 28]]],
  3:  [26, [[1, 44]]],
  4:  [18, [[2, 32]]],
  5:  [24, [[2, 43]]],
  6:  [16, [[4, 27]]],
  7:  [18, [[4, 31]]],
  8:  [22, [[2, 38], [2, 39]]],
  9:  [22, [[3, 36], [2, 37]]],
  10: [26, [[4, 43], [1, 44]]],
  11: [30, [[1, 50], [4, 51]]],
  12: [22, [[6, 36], [2, 37]]],
  13: [22, [[8, 37], [1, 38]]],
  14: [24, [[4, 40], [5, 41]]],
  15: [24, [[5, 41], [5, 42]]],
};

/** Centres of the alignment patterns, per version. */
const ALIGN = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
  7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
  11: [6, 30, 54], 12: [6, 32, 58], 13: [6, 34, 62], 14: [6, 26, 46, 66],
  15: [6, 26, 48, 70],
};

// ── GF(256), primitive polynomial 0x11D ─────────────────────────────────────
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();
const gfMul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

/**
 * Generator polynomial for `n` error-correction codewords: ∏(x − α^i).
 * Coefficients are DESCENDING (index 0 = the leading x^n term), which is the
 * order the division loop below indexes into. Building it ascending instead
 * yields a code whose data reads back perfectly and whose ECC is garbage — so
 * every scanner rejects it while the matrix looks entirely plausible.
 */
function genPoly(n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] ^= g[j];                          // x · g
      next[j + 1] ^= gfMul(g[j], EXP[i]);       // α^i · g
    }
    g = next;
  }
  return g;
}

/** Reed–Solomon remainder of `data` for `n` EC codewords. */
function ecBytes(data, n) {
  const g = genPoly(n);
  const rem = new Array(n).fill(0);
  for (const d of data) {
    const factor = d ^ rem[0];
    rem.shift();
    rem.push(0);
    for (let j = 0; j < n; j++) rem[j] ^= gfMul(g[j + 1], factor);
  }
  return rem;
}

/** UTF-8 bytes, so a label with an accent or a dash survives the round trip. */
function utf8(str) {
  return Array.from(new TextEncoder().encode(str));
}

/** Smallest version that fits `len` bytes at level M. */
function pickVersion(len, minVersion = 1) {
  for (let v = Math.max(1, minVersion); v <= 15; v++) {
    const [ec, groups] = EC_M[v];
    const cap = groups.reduce((s, [n, d]) => s + n * d, 0);
    // 4 bits mode + 8 or 16 bits length, rounded up to whole bytes.
    const header = 4 + (v < 10 ? 8 : 16);
    if (cap * 8 >= header + len * 8) return v;
    void ec;
  }
  throw new Error(`Too much data for a version-15 QR (${len} bytes).`);
}

/** Mode + length + payload + terminator + pad, then RS blocks, interleaved. */
function codewords(bytes, version) {
  const [ecPer, groups] = EC_M[version];
  const total = groups.reduce((s, [n, d]) => s + n * d, 0);

  const bits = [];
  const push = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); };
  push(0b0100, 4);                                  // byte mode
  push(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) push(b, 8);
  for (let i = 0; i < 4 && bits.length < total * 8; i++) bits.push(0);   // terminator
  while (bits.length % 8) bits.push(0);
  const data = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    data.push(b);
  }
  // Alternating pad bytes, per spec.
  const PAD = [0xec, 0x11];
  for (let i = 0; data.length < total; i++) data.push(PAD[i % 2]);

  // Split into blocks, compute EC per block, then interleave both.
  const blocks = [];
  let at = 0;
  for (const [count, dataLen] of groups) {
    for (let i = 0; i < count; i++) {
      const d = data.slice(at, at + dataLen);
      at += dataLen;
      blocks.push({ d, e: ecBytes(d, ecPer) });
    }
  }
  const out = [];
  const maxData = Math.max(...blocks.map(b => b.d.length));
  for (let i = 0; i < maxData; i++) for (const b of blocks) if (i < b.d.length) out.push(b.d[i]);
  for (let i = 0; i < ecPer; i++) for (const b of blocks) out.push(b.e[i]);
  return out;
}

/** BCH(15,5) format information, already XOR-masked. */
function formatBits(maskId) {
  const ECM = 0b00;                       // level M
  let v = (ECM << 3) | maskId;
  let d = v << 10;
  for (let i = 4; i >= 0; i--) if (d & (1 << (i + 10))) d ^= 0x537 << i;
  return ((v << 10) | d) ^ 0x5412;
}

/** BCH(18,6) version information (versions 7+). */
function versionBits(version) {
  let d = version << 12;
  for (let i = 5; i >= 0; i--) if (d & (1 << (i + 12))) d ^= 0x1f25 << i;
  return (version << 12) | d;
}

const MASKS = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/** The fixed patterns: finders, separators, timing, alignment, dark module. */
function skeleton(version) {
  const size = version * 4 + 17;
  const m = Array.from({ length: size }, () => new Array(size).fill(null));
  const fixed = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (r, c, v) => { if (r >= 0 && c >= 0 && r < size && c < size) { m[r][c] = v; fixed[r][c] = true; } };

  const finder = (top, left) => {
    for (let r = -1; r <= 7; r++) for (let c = -1; c <= 7; c++) {
      const on = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
                 (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
                 (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      set(top + r, left + c, on);
    }
  };
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) { set(6, i, i % 2 === 0); set(i, 6, i % 2 === 0); }

  const centres = ALIGN[version];
  for (const r of centres) for (const c of centres) {
    // Alignment patterns never overlap a finder.
    if ((r <= 8 && c <= 8) || (r <= 8 && c >= size - 9) || (r >= size - 9 && c <= 8)) continue;
    for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) {
      set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
    }
  }

  // Reserve the format areas (written after masking). Index 6 is skipped in
  // both runs: row 8 / column 8 cross the timing patterns there, and blanking
  // those two modules quietly breaks every code.
  for (let i = 0; i < 9; i++) { if (i !== 6) { set(8, i, false); set(i, 8, false); } }
  // Second copy: 8 modules along row 8 at the right, but only 7 up the left of
  // the bottom-left finder — the 8th is the dark module, which is not format.
  for (let i = 0; i < 8; i++) set(8, size - 1 - i, false);
  for (let i = 0; i < 7; i++) set(size - 1 - i, 8, false);

  set(size - 8, 8, true);                                  // dark module (after, so nothing blanks it)

  if (version >= 7) {
    for (let i = 0; i < 18; i++) {
      const r = Math.floor(i / 3), c = i % 3;
      set(size - 11 + c, r, false);
      set(r, size - 11 + c, false);
    }
  }
  return { m, fixed, size };
}

/** Zigzag data placement, right to left, skipping the vertical timing column. */
function placeData(m, fixed, size, bytes) {
  let bit = 0;
  const next = () => {
    const i = bit >> 3;
    const b = i < bytes.length ? (bytes[i] >> (7 - (bit & 7))) & 1 : 0;
    bit++;
    return b === 1;
  };
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;                     // column 6 is timing
    for (let v = 0; v < size; v++) {
      const row = upward ? size - 1 - v : v;
      for (const col of [right, right - 1]) {
        if (fixed[row][col]) continue;
        m[row][col] = next();
      }
    }
    upward = !upward;
  }
}

/** The four penalty rules — lower is a more scannable code. */
function penalty(m, size) {
  let p = 0;
  const runScore = (line) => {
    let s = 0, run = 1;
    for (let i = 1; i < line.length; i++) {
      if (line[i] === line[i - 1]) run++;
      else { if (run >= 5) s += run - 2; run = 1; }
    }
    if (run >= 5) s += run - 2;
    return s;
  };
  for (let i = 0; i < size; i++) {
    p += runScore(m[i]);
    p += runScore(m.map(r => r[i]));
  }
  for (let r = 0; r < size - 1; r++) for (let c = 0; c < size - 1; c++) {
    const a = m[r][c];
    if (a === m[r][c + 1] && a === m[r + 1][c] && a === m[r + 1][c + 1]) p += 3;
  }
  const PAT1 = [true, false, true, true, true, false, true, false, false, false, false];
  const PAT2 = [false, false, false, false, true, false, true, true, true, false, true];
  const hit = (get, i, j) => PAT1.every((v, k) => get(i, j + k) === v) || PAT2.every((v, k) => get(i, j + k) === v);
  for (let r = 0; r < size; r++) for (let c = 0; c + 10 < size; c++) {
    if (hit((i, j) => m[i][j], r, c)) p += 40;
    if (hit((i, j) => m[j][i], r, c)) p += 40;
  }
  let dark = 0;
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (m[r][c]) dark++;
  p += Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5) * 10;
  return p;
}

/**
 * Encode `text` as a QR matrix.
 * @returns {{size:number, version:number, modules:boolean[][]}}
 */
export function qrMatrix(text, { minVersion = 1, forceMask = null } = {}) {
  const bytes = utf8(String(text));
  const version = pickVersion(bytes.length, minVersion);
  const data = codewords(bytes, version);

  let best = null;
  for (let mask = 0; mask < 8; mask++) {
    if (forceMask != null && mask !== forceMask) continue;
    const { m, fixed, size } = skeleton(version);
    placeData(m, fixed, size, data);
    for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) {
      if (!fixed[r][c] && MASKS[mask](r, c)) m[r][c] = !m[r][c];
    }
    // Format info (both copies).
    const f = formatBits(mask);
    for (let i = 0; i < 15; i++) {
      const on = ((f >> i) & 1) === 1;
      // First copy: the LSB sits at [0][8] and runs DOWN the column, then along
      // row 8 leftwards. Mirroring these two runs produces a code with perfect
      // structure that no scanner will read — the format is what tells a reader
      // the mask, so getting it backwards corrupts every module.
      if (i < 6) m[i][8] = on;
      else if (i === 6) m[7][8] = on;
      else if (i === 7) m[8][8] = on;
      else if (i === 8) m[8][7] = on;
      else m[8][14 - i] = on;

      // Second copy runs the other way round: bits 0–7 leftwards along row 8
      // from the right edge, then bits 8–14 down column 8 to the bottom. It
      // stops short of [size-8][8], which is the dark module, not format.
      if (i < 8) m[8][size - 1 - i] = on;
      else m[size - 15 + i][8] = on;
    }
    if (version >= 7) {
      const vb = versionBits(version);
      for (let i = 0; i < 18; i++) {
        const on = ((vb >> i) & 1) === 1;
        const r = Math.floor(i / 3), c = i % 3;
        m[size - 11 + c][r] = on;
        m[r][size - 11 + c] = on;
      }
    }
    const score = penalty(m, size);
    if (!best || score < best.score) best = { score, modules: m, size };
  }
  return { size: best.size, version, modules: best.modules };
}

/**
 * Encode `text` as an SVG string. Vector, so it stays crisp at any print size.
 * `quiet` is the mandatory light margin (4 modules); scanners need it.
 */
export function qrSvg(text, { size = 160, quiet = 4, dark = '#000', light = '#fff' } = {}) {
  const { modules, size: n } = qrMatrix(text);
  const total = n + quiet * 2;
  // One path of rectangles beats n² <rect> elements — a sheet of 200 codes
  // otherwise becomes a megabyte of DOM that the print dialog chews on.
  let d = '';
  for (let r = 0; r < n; r++) {
    let c = 0;
    while (c < n) {
      if (!modules[r][c]) { c++; continue; }
      let run = 1;
      while (c + run < n && modules[r][c + run]) run++;
      d += `M${c + quiet} ${r + quiet}h${run}v1h-${run}z`;
      c += run;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges">` +
    `<rect width="${total}" height="${total}" fill="${light}"/>` +
    `<path d="${d}" fill="${dark}"/></svg>`;
}

/**
 * A data: URL of the SVG, for an `<img>` or a print sheet.
 *
 * ⚠️ BARE — no UMaps mark. Nothing in the product calls this: every surface
 * goes through `utils/qrBadge.js`. Kept because an `<img src>` is occasionally
 * the only shape that fits, but a new caller wants `qrBadgeSvgEmbedded` and a
 * data: URL built from THAT, or it will be the one unbranded code on campus.
 */
export function qrDataUrl(text, opts) {
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(qrSvg(text, opts));
}

// Test hooks — exported so the encoder's stages can be checked against a
// known-good reference code, which is how the format-placement bugs were found.
export { codewords as _codewords, skeleton as _skeleton, MASKS as _MASKS };
